/**
 * The mission stepper (missions vision, "Mission stages"): Prepare, Work, Check, Review, Land, one
 * component for the run page, the item drawer and the rows. Each stage draws with the work-state
 * icons (status.md), so a stage reads like the same state anywhere else: hollow while waiting,
 * the turning ring while under way, the hand when it waits on a person, the check when done, the
 * cross when it failed, a faint hollow when skipped. Colour only on the icons and dots.
 *
 *   <MissionStages stages={missionStages(mission, view)} />                the stepper, with labels
 *   <MissionStages stages={...} variant="compact" />                       five dots for a row
 */
import { Tooltip, WORK_TONE_CLASS, WorkStateIcon, cn } from '@orgus/ui';
import type { WorkState, WorkTone } from '@orgus/ui';
import { STAGE_STATE_LABEL, currentStage } from './stages.ts';
import type { MissionStage, StageState } from './stages.ts';

const STAGE_WORK: Record<StageState, WorkState> = { waiting: 'stale', current: 'running', attention: 'needs-you', done: 'landed', failed: 'failed', skipped: 'stale' };
const STAGE_TONE: Record<StageState, WorkTone> = { waiting: 'neutral', current: 'working', attention: 'attention', done: 'success', failed: 'danger', skipped: 'neutral' };

/** "Work: under way. Prepare done, Work under way, Check not started, ..." for a screen reader and the tooltip. */
export function stagesLabel(stages: readonly MissionStage[]): string {
  const at = currentStage(stages);
  return `${at.label}: ${STAGE_STATE_LABEL[at.state].toLowerCase()}. ${stages.map((s) => `${s.label} ${STAGE_STATE_LABEL[s.state].toLowerCase()}`).join(', ')}`;
}

export function MissionStages({ stages, variant = 'full', className }: { stages: readonly MissionStage[]; variant?: 'full' | 'compact'; className?: string }) {
  const label = stagesLabel(stages);
  if (variant === 'compact') {
    return (
      <Tooltip content={label}>
        <span role="img" aria-label={`Stages: ${label}`} tabIndex={0} className={cn('inline-flex shrink-0 items-center gap-1', className)} data-mission-stages="compact">
          {stages.map((s) => (
            <span
              key={s.id}
              className={cn(
                'size-2 shrink-0 rounded-pill',
                s.state === 'waiting' ? 'bg-border-strong' : s.state === 'skipped' ? 'bg-border' : WORK_TONE_CLASS[STAGE_TONE[s.state]].bar,
              )}
            />
          ))}
        </span>
      </Tooltip>
    );
  }
  return (
    <ol aria-label={`Stages: ${label}`} className={cn('flex min-w-0 items-center gap-2 max-sm:items-start', className)} data-mission-stages="full">
      {stages.map((s, i) => {
        const reached = s.state !== 'waiting' && s.state !== 'skipped';
        const now = s.state === 'current' || s.state === 'attention' || s.state === 'failed';
        const prev = stages[i - 1];
        // A skipped stage passes the line on: the run went through it to a stage it reached.
        const lit = !!prev && (prev.state === 'done' || (prev.state === 'skipped' && reached));
        return (
          // Every stage after the first grows by the same share, so the lines between them are even
          // whatever the labels' lengths, and a label is never drawn over by the next line.
          <li key={s.id} className={cn('flex min-w-0 items-center gap-2 max-sm:items-start', i > 0 && 'flex-auto')} aria-current={now ? 'step' : undefined}>
            {/* The line into a stage is drawn once the stage before it is done. On a phone it meets the icons' middle. */}
            {i > 0 && <span className={cn('h-px min-w-3 flex-1 rounded-pill max-sm:mt-2', lit ? 'bg-fg-faint' : 'bg-border')} aria-hidden />}
            {/* On a phone each label sits under its icon, so every stage keeps its word. */}
            <span className={cn('flex shrink-0 items-center gap-2 max-sm:flex-col max-sm:gap-1', s.state === 'skipped' && 'opacity-60')}>
              <WorkStateIcon state={STAGE_WORK[s.state]} />
              <span className={cn('truncate text-caption', now ? cn('font-medium', s.state === 'current' ? 'text-fg' : WORK_TONE_CLASS[STAGE_TONE[s.state]].fg) : reached ? 'text-fg-muted' : 'text-fg-subtle')}>{s.label}</span>
              <span className="sr-only">{STAGE_STATE_LABEL[s.state]}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
